import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

interface Option {
  id: string;
  fullName: string;
  registrationNumber: string;
  contactNumber: string;
  category: string;
  division: string;
  zone: string;
}

interface CustomSearchDropdownProps {
  options: Option[];
  value: string;
  onSelect: (value: string) => void;
  onSearch: (searchTerm: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const CustomSearchDropdown: React.FC<CustomSearchDropdownProps> = ({
  options,
  value,
  onSelect,
  onSearch,
  placeholder = "Select an option",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find(option => option.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Immediate API call without debouncing
  useEffect(() => {
    if (isOpen) {
      onSearch(searchTerm);
    }
  }, [searchTerm, onSearch, isOpen]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (options.length > 0) {
          setHighlightedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (options.length > 0) {
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : options.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelect = (optionId: string) => {
    onSelect(optionId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('');
    setSearchTerm('');
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) {
      setIsOpen(true);
      setSearchTerm('');
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <div 
      ref={dropdownRef} 
      className={`relative ${className}`}
    >
      {/* When dropdown is closed, show the trigger button */}
      {!isOpen && (
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`
            w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm text-left transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-300 hover:shadow-md'}
          `}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex items-center min-w-0 flex-1">
            {selectedOption ? (
              <div className="flex flex-col">
                <span className="truncate text-sm font-semibold text-gray-900">
                  {selectedOption.fullName}
                </span>
                {/* <span className="truncate text-xs text-gray-500">
                  {selectedOption.registrationNumber} • {selectedOption.contactNumber}
                </span> */}
              </div>
            ) : (
              <span className="text-gray-400 text-sm font-medium">{placeholder}</span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-3">
            {/* {selectedOption && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )} */}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </button>
      )}

      {/* When dropdown is open, show the search input and dropdown */}
      {isOpen && (
        <div className="w-full">
          {/* Fixed Search Input Header - Now with sticky positioning */}
          <div className="sticky top-0 z-50 relative bg-white border border-gray-200 rounded-t-lg shadow-sm">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Search by name, registration, or phone..."
              className="w-full pl-11 pr-12 py-3 border-0 rounded-t-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-0"
            />
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setSearchTerm('');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ChevronDown className="h-4 w-4 text-gray-400 rotate-180" />
            </button>
          </div>

          {/* Dropdown Panel with Fixed Positioning */}
          <div className="absolute z-50 w-full bg-white border-l border-r border-b border-gray-200 rounded-b-lg shadow-lg max-h-[85vh] overflow-hidden">
            {/* Results Count Header */}
            {options.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                <span className="text-xs font-medium text-gray-600">
                  {options.length} consumer{options.length !== 1 ? 's' : ''} found
                </span>
              </div>
            )}
            
            {/* Scrollable Options List */}
            <div className="max-h-[calc(85vh-40px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <ul 
                ref={listRef}
                className="divide-y divide-gray-50"
                role="listbox"
              >
                {options.length > 0 ? (
                  options.map((option, index) => (
                    <li
                      key={option.id}
                      onClick={() => handleSelect(option.id)}
                      className={`
                        px-4 py-4 cursor-pointer transition-all duration-150
                        ${highlightedIndex === index ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'}
                        ${value === option.id ? 'bg-blue-100 border-l-4 border-blue-600' : ''}
                      `}
                      role="option"
                      aria-selected={value === option.id}
                    >
                      <div className="space-y-1.5">
                        {/* Primary Info Row */}
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-gray-900 truncate max-w-[60%]">
                            {option.fullName}
                          </span>
                          
                          <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 font-medium">
                              📞 {option.contactNumber}
                            </span>
                          <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {option.registrationNumber}
                          </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {option.category}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {option.division}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {option.zone}
                            </span>
                          </div>                   
                        </div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="h-8 w-8 text-gray-300" />
                      <span className="text-sm font-medium text-gray-500">
                        {searchTerm ? 'No consumers found' : 'Start typing to search'}
                      </span>
                      {searchTerm && (
                        <span className="text-xs text-gray-400">
                          Try searching by name, registration number, or phone
                        </span>
                      )}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSearchDropdown;